import React, { FC, InputHTMLAttributes, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { cn } from '../../../helpers';
import styles from './checkbox.module.scss';

interface ICheckboxProps extends InputHTMLAttributes<HTMLElement> {
    autofocus?: boolean;
    indeterminate?: boolean;
    label?: string;
    notLocalize?: boolean;
    touched?: boolean;
    wrapped?: boolean;
}

/**
 * Checkbox
 * Genric UI Checkbox Component
 * @returns JSX.Element
 */
export const Checkbox: FC<ICheckboxProps> = ({
    autofocus,
    indeterminate,
    name,
    wrapped,
    checked,
    touched,
    title,
    disabled,
    required,
    id,
    ...props
}) => {
    const checkboxLabelRef = React.createRef<HTMLLabelElement>();
    const checkboxRef = React.createRef<HTMLInputElement>();

    useEffect(() => {
        if (autofocus) {
            checkboxLabelRef.current.focus();
        }
    }, [checkboxLabelRef, autofocus]);

    useEffect(() => {
        if (indeterminate !== undefined) {
            checkboxRef.current.indeterminate = indeterminate;
        }
    }, [checkboxRef, indeterminate]);

    const getLabel = (props: ICheckboxProps): JSX.Element => {
        if (props.notLocalize) {
            return <span>{props.label}</span>;
        }
        return <FormattedMessage id={props.label} />;
    };

    return (
        <div
            onClick={(e): void => e.stopPropagation()}
            className={cn(props.className, {
                [styles.checkboxWrapper]: wrapped,
            })}
            aria-label={props['aria-label']}
        >
            <input
                type="checkbox"
                name={name}
                className={styles.checkboxInput}
                checked={checked}
                ref={checkboxRef}
                disabled={disabled}
                onChange={props.onChange}
                title={title ? title : ''}
                id={id ? String(id) : name}
            />
            <label
                ref={checkboxLabelRef}
                htmlFor={id ? String(id) : name}
                className={[
                    styles.checkboxLabel,
                    wrapped ? styles.checkboxWrappedLabel : '',
                    required && touched && !checked ? styles.error : '',
                ].join(' ')}
                aria-checked={checked}
            >
                {props.label ? (
                    getLabel(props)
                ) : (
                    <span className="d-none">
                        <FormattedMessage id="CHECKBOX_ICON" />
                    </span>
                )}
            </label>
        </div>
    );
};
